import React from 'react'
import { Tooltip, Typography, Box } from '@material-ui/core'
import { motion } from 'framer-motion'
import { ReviewChip, FormChipContainer } from '../styles/StyledComponents'

const DiseaseChips = ({ diseases, getDiseaseDetails, maxVisible = 3 }) => {
  if (!diseases?.length) return null
  return (
    <FormChipContainer
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        width: '100%',
        maxWidth: '100%',
        justifyContent: 'flex-start',
        gap: 1.5,
      }}
    >
      {diseases.slice(0, maxVisible).map((disease, index) => {
        const diseaseDetails = getDiseaseDetails(disease)
        return (
          <Box key={index} sx={{ flex: '1 1 0', minWidth: 0, maxWidth: 'calc(25% - 12px)', mb: 1 }}>
            <Tooltip
              title={
                <Box sx={{ p: 1.5, maxWidth: '400px' }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, mb: 1.5, fontSize: '0.95rem', lineHeight: 1.3, color: 'white' }}
                  >
                    {diseaseDetails.name}
                  </Typography>
                  {diseaseDetails.description && (
                    <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.4, color: 'rgba(255, 255, 255, 0.85)' }}>
                      {diseaseDetails.description}
                    </Typography>
                  )}
                  <Box sx={{ mb: 1.5 }}>
                    {diseaseDetails.omim && (
                      <Typography variant="body2" sx={{ mb: 0.5, color: 'rgba(255, 255, 255, 0.9)' }}>
                        <Box component="span" sx={{ fontWeight: 600, color: '#ffb300' }}>
                          OMIM:
                        </Box>{' '}
                        <Box component="span" sx={{ fontFamily: 'fontFamilyMono', fontSize: '0.85rem' }}>
                          {diseaseDetails.omim}
                        </Box>
                      </Typography>
                    )}
                    {diseaseDetails.inheritance && (
                      <Typography variant="body2" sx={{ mb: 0.5, color: 'rgba(255, 255, 255, 0.9)' }}>
                        <Box component="span" sx={{ fontWeight: 600, color: '#00bcd4' }}>
                          Inheritance:
                        </Box>{' '}
                        {diseaseDetails.inheritance}
                      </Typography>
                    )}
                    {diseaseDetails.prevalence && (
                      <Typography variant="body2" sx={{ mb: 0.5, color: 'rgba(255, 255, 255, 0.9)' }}>
                        <Box component="span" sx={{ fontWeight: 600, color: '#2196f3' }}>
                          Prevalence:
                        </Box>{' '}
                        {diseaseDetails.prevalence}
                      </Typography>
                    )}
                  </Box>
                  {diseaseDetails.genes?.length > 0 && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          display: 'block',
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: '0.75rem',
                        }}
                      >
                        Associated Genes:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {diseaseDetails.genes.slice(0, 4).map((gene, geneIndex) => (
                          <Box
                            key={geneIndex}
                            sx={{
                              px: 1,
                              py: 0.25,
                              backgroundColor: '#00bcd420',
                              borderRadius: '12px',
                              border: '1px solid #00bcd440',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#00bcd4',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                fontFamily: 'fontFamilyMono',
                              }}
                            >
                              {gene}
                            </Typography>
                          </Box>
                        ))}
                        {diseaseDetails.genes.length > 4 && (
                          <Box
                            sx={{
                              px: 1,
                              py: 0.25,
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              borderRadius: '12px',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.75rem', fontWeight: 500 }}
                            >
                              +{diseaseDetails.genes.length - 4} more
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                  {diseaseDetails.synonyms?.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          display: 'block',
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: '0.75rem',
                        }}
                      >
                        Also known as:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {diseaseDetails.synonyms.slice(0, 3).map((synonym, synIndex) => (
                          <Box
                            key={synIndex}
                            sx={{
                              px: 1,
                              py: 0.25,
                              backgroundColor: '#2196f320',
                              borderRadius: '12px',
                              border: '1px solid #2196f340',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: '#2196f3', fontSize: '0.75rem', fontWeight: 500 }}
                            >
                              {synonym}
                            </Typography>
                          </Box>
                        ))}
                        {diseaseDetails.synonyms.length > 3 && (
                          <Box
                            sx={{
                              px: 1,
                              py: 0.25,
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              borderRadius: '12px',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.75rem', fontWeight: 500 }}
                            >
                              +{diseaseDetails.synonyms.length - 3} more
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                  {diseaseDetails.category && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" sx={{ color: '#ffb300', fontWeight: 600, fontSize: '0.75rem' }}>
                        Category: {diseaseDetails.category}
                      </Typography>
                    </Box>
                  )}
                </Box>
              }
              placement="top"
              arrow
            >
              <motion.div whileHover={{ scale: 1.05 }} transition={{ type: 'spring', stiffness: 400, damping: 20 }}>
                <ReviewChip
                  label={String(diseaseDetails.name)}
                  chipColor="info"
                  size="small"
                  sx={{
                    width: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': { backgroundColor: '#2196f320', boxShadow: `0 2px 8px #2196f330` },
                  }}
                />
              </motion.div>
            </Tooltip>
          </Box>
        )
      })}
      {diseases.length > maxVisible && (
        <Box sx={{ flex: '1 1 0', minWidth: 0, maxWidth: 'calc(25% - 12px)', mb: 1 }}>
          <Tooltip
            title={
              <Box sx={{ p: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Additional Diseases
                </Typography>
                {diseases.slice(maxVisible).map((disease, index) => {
                  const diseaseDetails = getDiseaseDetails(disease)
                  return (
                    <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                      • {diseaseDetails.name}
                    </Typography>
                  )
                })}
              </Box>
            }
            placement="top"
            arrow
          >
            <ReviewChip
              label={`+${diseases.length - maxVisible} more`}
              chipColor="default"
              size="small"
              sx={{ width: '100%', cursor: 'pointer' }}
            />
          </Tooltip>
        </Box>
      )}
    </FormChipContainer>
  )
}

export default DiseaseChips
